import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FileUploadData, UiFormField } from '../../utils/CommonTypes/UserTypes';
import StoreX from '../../redux/oldStore';
import Icon, { IconType } from '../Icon/Icon';
import './AudioClipInput.css';

const MAX_RECORD_SECONDS = 15;
const ALLOWED_EXTS = ['.mp3', '.wav', '.webm', '.ogg', '.oga', '.m4a', '.aac'];

interface AudioClipInputProps {
  field: UiFormField;
  formValues: Record<string, string[]>;
  handleFieldChange: (key: string, value: string[]) => void;
  handleFileChange: (key: string, file?: File) => void;
  readOnly?: boolean;
}

const pickRecorderMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
};

const extForMime = (mime: string) => {
  if (mime.indexOf('mp4') >= 0) return '.m4a';
  if (mime.indexOf('ogg') >= 0) return '.ogg';
  if (mime.indexOf('mpeg') >= 0) return '.mp3';
  return '.webm';
};

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const parseClipSrc = (field: UiFormField, formValues: Record<string, string[]>): string => {
  const current = formValues[field.Key];
  if (current) {
    const v = current[0];
    if (!v) return '';
    if (v.charAt(0) === '{') {
      try {
        const parsed = JSON.parse(v) as FileUploadData;
        return parsed.dataUrl ?? '';
      } catch {
        return '';
      }
    }
    return v;
  }
  const stored = field.Value ?? [];
  if (stored.length >= 3 && stored[0]) {
    return StoreX.BuildUrl(stored[0]);
  }
  return stored[0] ?? '';
};

const AudioClipInput = (props: AudioClipInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [canRecord, setCanRecord] = useState(false);
  const src = parseClipSrc(props.field, props.formValues);

  const maxBytes = Math.max(1, Math.round((props.field.FileSizeMaxMB ?? 1) * 1024 * 1024));
  const maxLabel = `${props.field.FileSizeMaxMB ?? 1} MB`;

  useEffect(() => {
    const supported =
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined';
    setCanRecord(supported);
    return () => {
      stopTracks();
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  };

  const allowedExts = () => {
    const fromField = (props.field.FileExts ?? '')
      .split(',')
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
    return fromField.length ? fromField : ALLOWED_EXTS;
  };

  const isAllowedFile = (file: File) => {
    const name = file.name || '';
    const ext = name.substring(name.lastIndexOf('.')).toLowerCase();
    if (file.size > maxBytes) {
      toast.warning(`The file "${name || 'recording'}" is too large. The max file size is ${maxLabel}.`);
      return false;
    }
    if (ext && allowedExts().indexOf(ext) < 0) {
      toast.warning(`Please choose an audio file (${allowedExts().join(', ')}).`);
      return false;
    }
    return true;
  };

  const commitFile = async (file: File) => {
    if (!isAllowedFile(file)) return;
    const dataUrl = await fileToDataUrl(file);
    const payload: FileUploadData = { caption: '', filename: file.name, dataUrl };
    props.handleFileChange(props.field.Key, file);
    props.handleFieldChange(props.field.Key, [JSON.stringify(payload)]);
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    try {
      await commitFile(file);
    } catch {
      toast.error('Could not read that audio file.');
    }
  };

  const startRecording = async () => {
    if (props.readOnly || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickRecorderMime();
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 48000 }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = async () => {
        const type = recorder.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        stopTracks();
        setRecording(false);
        setElapsed(0);
        if (timerRef.current) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (blob.size < 1) {
          toast.warning('No audio was captured. Please try again.');
          return;
        }
        const file = new File([blob], `name-clip${extForMime(type)}`, { type });
        try {
          await commitFile(file);
        } catch {
          toast.error('Could not save the recording.');
        }
      };
      recorder.start();
      setRecording(true);
      setElapsed(0);
      const startedAt = Date.now();
      timerRef.current = window.setInterval(() => {
        const s = Math.floor((Date.now() - startedAt) / 1000);
        setElapsed(s);
        if (s >= MAX_RECORD_SECONDS) {
          if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
          if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }, 250);
    } catch {
      toast.error('Could not access the microphone. Check browser permissions, or upload a file instead.');
      stopTracks();
      setRecording(false);
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const removeClip = () => {
    if (props.readOnly) return;
    props.handleFileChange(props.field.Key);
    props.handleFieldChange(props.field.Key, ['']);
  };

  return (
    <div className={`audio-clip-input fk-${props.field.Key} ${props.field.Required ? 'x-required' : ''}`}>
      {src && (
        <audio className="audio-clip-input-player" controls src={src} preload="metadata">
          Your browser does not play this audio format.
        </audio>
      )}
      {!props.readOnly && (
        <div className="audio-clip-input-actions">
          <input
            id={props.field.Key}
            ref={fileInputRef}
            type="file"
            accept={`${allowedExts().join(',')},audio/*`}
            className="hide"
            onChange={onPickFile}
          />
          <button
            type="button"
            className="btn btn-default"
            disabled={recording}
            onClick={() => fileInputRef.current?.click()}>
            <Icon type={IconType.upload} /> Upload
          </button>
          {canRecord && !recording && (
            <button type="button" className="btn btn-primary" onClick={startRecording}>
              <Icon type={IconType.microphone} /> Record now
            </button>
          )}
          {canRecord && recording && (
            <button type="button" className="btn btn-danger" onClick={stopRecording}>
              <Icon type={IconType.microphoneSlash} /> Stop ({MAX_RECORD_SECONDS - elapsed}s)
            </button>
          )}
          {src && (
            <button type="button" className="btn btn-link" onClick={removeClip}>
              Remove
            </button>
          )}
        </div>
      )}
      {recording && <div className="audio-clip-input-rec">Recording… speak your name</div>}
      {!src && !recording && <div className="small text-muted">Max {maxLabel}. About {MAX_RECORD_SECONDS} seconds.</div>}
    </div>
  );
};

export default AudioClipInput;
